(function () {

    'use strict';

    const METRIC_S_THRESHOLD = 5;
    const METRIC_M_THRESHOLD = 100;
    const METRIC_L_THRESHOLD = 500;
    const METRIC_XL_THRESHOLD = 10000;

    // NOTE we do this to maintain the illusion of the module pattern since we
    // have to load this file on the server as well.
    const definition = function (utils, App, enums, Backbone, Marionette, $, _, d3, moment) {

        const formatter = App.formatter = {};

        /**
         * Strips trailing zeros, and removes decimal point if need be.
         * @param {String} str
         * @returns {String} A number removed of its trailing zeros.
         */
        const stripTrailingZeros = formatter.stripTrailingZeros = function (str) {

            const parts = str.split('.');

            const stripped = _.dropRightWhile(parts[1], function (c) {

                return (c === '0');
            }).join('');

            return _.head(parts) + (_.size(stripped) > 0 ? '.' : '') + stripped;
        };

        /**
         * Formats a value as a percentage.
         * @param val The value to format.
         * @returns {String} e.g., 30.0%
         */
        formatter.formatPercentage = function (val) {

            return `${(val.toPrecision(2) * 100).toPrecision(3)}%`;
        };

        /**
         * Formats a number for currency intelligently displaying decimal places.
         * @param val The value to format.
         * @returns {String} e.g., 199, 1.50, 2,001, 10,000.99
         */
        formatter.formatCurrency = function (val) {

            const comma = d3.format(',');
            const str = comma(val);
            const fixed = d3.format('0,.2f');

            return (str.indexOf('.') === -1 ? str : fixed(val));
        };

        formatter.formatAmountAndCurrency = function (val, options) {
            const amount = _.get(val, 'value');
            if (_.isUndefined(amount)) {
                return '--';
            }
            const currency = val.currency;

            const decimals = _.get(options, 'decimals', 2);
            const formatStr = `0,.${decimals}f`;
            let amountStr;

            if (_.get(options, 'abbreviate', false)) {
                amountStr = formatter.formatAbbreviated(amount, formatStr, options);
            } else {
                amountStr = d3.format(formatStr)(amount);
            }

            switch (currency) {
                case 'USD':
                    return `$${amountStr}`;
                default:
                    return _.compact([
                        amountStr,
                        (_.get(options, 'currency') !== false ? currency : null)
                    ]).join(' ');
            }
        };

        formatter.formatSeconds = function (val, options) {
            return formatter.formatTimeInterval(val * 1000, options);
        };

        /**
         * Formats milliseconds into a human digestible interval
         */
        formatter.formatTimeInterval = function (ms, options) {

            let totalSeconds = parseInt(ms / 1000, 10);

            if (isNaN(totalSeconds)) {
                totalSeconds = 0;
            }

            let negative = false;
            if (totalSeconds < 0) {
                totalSeconds *= -1;
                negative = true;
            }

            const hours = Math.floor(totalSeconds / 3600);
            const minutes = Math.floor(totalSeconds / 60) % 60;
            const seconds = totalSeconds % 60;

            const pad = function (n) { return (n < 10 ? ['0', n] : [n]).join(''); };

            const result = `${(negative ? '-' : '') + (hours > 0 ? `${hours}:` : '') + pad(minutes)}:${pad(seconds)}`;

            return result;
        };

        /**
         * Formats a string as ellipsified if it exceeds length.
         * @param length The length at which to ellipsify.
         * @param val The value to ellipsify.
         * @returns {String}
         */
        formatter.ellipsify = function (length, val) {
            if (!val || val.length <= length) {
                return val;
            }

            return `${val.substring(0, length + 1)}...`;
        };

        /**
         * Formats a string as an ellipsified URL when too long.
         * @param val The value to ellipsify when too long.
         * @returns {String}
         */
        formatter.urlShort = function (val) {
            let outstr = '';

            if (!_.exists(val)) {
                return outstr;
            }

            const str = val.toString();
            const len = str.length;
            const MAX_LENGTH = 22;

            if (len > MAX_LENGTH) {

                outstr += str.slice(0, 10);
                outstr += '...';
                outstr += str.slice(len - 10, len);
            } else {

                outstr = str;
            }

            return outstr;
        };

        /**
         * Format a numeric value as an absolute percentage.
         * @param val The numeric value to format.
         * @returns {string}
         */
        formatter.absolutePercent = function (val, options) {
            options = (options || {});

            if (isNaN(parseFloat(val))) {
                return '--%';
            }

            let percentage = val * 100;
            if (!options.tick && !_.has(options, 'decimals') && _.isNumber(percentage)) {

                if (percentage !== 0 && Math.abs(percentage) < 0.1) {
                    options.decimals = 3;
                    percentage = Number(percentage.toPrecision(2));
                }

                if (percentage === 1) {
                    options.decimals = 2;
                    options.preferWhole = false;
                }
            }

            if (_.isFinite(options.roundAboveThreshold)) {
                if (percentage > options.roundAboveThreshold) {
                    options.decimals = 0;
                }
            }

            const abbreviated = formatter.formatAbbreviated(percentage, null, options);

            let prefix = '';
            const min = (1 / Math.pow(10, _.get(options, 'decimals', 2)));

            if (!options.baseline && percentage !== 0 && Math.abs(percentage) < min) {
                if (_.isFunction(options.smallValueSignFcn)) {
                    prefix = options.smallValueSignFcn(percentage);
                }
            } else if (_.isFunction(options.signFcn)) {
                prefix = options.signFcn(percentage);
            }

            return `${prefix + abbreviated}%`;
        };

        formatter.changeSignPrefix = function (v) {
            return (v < 0 || !_.isFinite(v) ? '' : '+');
        };

        formatter.relativePercent = function (val, options) {
            if (_.isArray(val)) {
                val = _.head(val);
            }

            if (val > -0.001 && val < 0.001 && val) {
                let sign;
                if (options && options.signFcn) {
                    sign = options.signFcn(val);
                } else {
                    sign = (Math.sign(val) === 1 ? '+' : '-');
                }
                return `< ${sign}0.1%`;
            }

            options = _.extend({
                decimals: 0,
                signFcn: formatter.changeSignPrefix,
                smallValueSignFcn: formatter.changeSignPrefix,
                abbreviate: (val > 1),
                roundAboveThreshold: 100,
                tick: true
            }, options);

            return formatter.absolutePercent(val, options);
        };

        formatter.percent = function (val, options) {
            const tick = _.get(options, 'tick', false);
            const abbreviate = _.get(options, 'abbreviate', tick);

            if (!tick && !abbreviate) {
                return formatter.formats.percent(val);
            }

            const extensions = {};
            if (!tick) {
                extensions.decimals = 3;
                extensions.signFcn = formatter.changeSignPrefix;
            }

            return formatter.absolutePercent(val, _.extend(extensions, options));
        };

        /**
         * Formats a numeric value in an abbreviated, human digestible form.
         * @param val The numeric value to format.
         * @param [format] An optional format string to use when values are less than 1,000
         * @param {String} e.g., 1.5, 14.3, 110.1, 1.1k, 10k, 25M
         */
        formatter.formatAbbreviated = function (val, format, options) {
            let str;
            let magnitude;

            options = _.defaults({}, options, {
                trailingZeros: true,
                preferWhole: true
            });

            // check if val can't be converted to a number, and treat Infinity
            // as NaN as well
            if (isNaN(Number(val)) || val === Infinity) {
                return val;
            }

            if (_.isNull(val)) {
                return '--';
            }

            val = Number(val);

            if (_.isFunction(format)) {

                return format(val, options);
            }

            if (Math.abs(val) >= 1000 && !options.skipFormat) {
                const precision = options.precision || 3;
                // this is how we get M, k, etc. formatting in d3
                let prefix = d3.formatPrefix(val);
                magnitude = val / prefix.scale(val);
                prefix = d3.formatPrefix(magnitude * Math.round(prefix.scale(val)));
                const result = prefix.scale(val).toPrecision(precision);
                return (!options.trailingZeros ? stripTrailingZeros(result) : result) + prefix.symbol;
            }

            if (format) {
                return d3.format(format)(val);
            }

            const min = 1 / Math.pow(10, _.get(options, 'decimals', 2));
            if (!options.minGoesToZero && !options.tick && (!options.baseline || !_.has(options, 'decimals')) && val !== 0 && Math.abs(val) < min) {
                return `<${min.toFixed(_.get(options, 'decimals', 2))}`;
            } else if (options.minGoesToZero && Math.abs(val) < min) {
                return 0;
            }

            if (options.max) {
                options.decimals = (options.max < 1 ? 1 : 0);
            }

            const getDecimals = function () {
                if (_.has(options, 'decimals')) {
                    return options.decimals;
                }

                const magnitude = Math.max(val.magnitude(), 0);

                if (magnitude > 0) {
                    if (magnitude === 2) {
                        return 0;
                    }
                    return 1;
                }

                return 2;
            };

            if (!options.tick) {

                str = d3.format(val % 1 || !options.preferWhole ? `,.${getDecimals()}f` : ',')(val);
            } else {
                const sigFigs = options.precision || 3;
                str = val.toSignificantFigures(sigFigs).toString();
            }

            if (!options.trailingZeros) {
                str = stripTrailingZeros(str);
            }

            return str;
        };

        /**
         * Much like formatter.formatAbbreviated, but always includes a leading +/- sign
         * for non-zero values.
         */
        formatter.formatAbbreviatedWithSign = function (val, format, options) {
            return (val > 0 ? '+' : '') + formatter.formatAbbreviated(val, format, options);
        };

        formatter.formatMetricRatioToText = function (metricRatio, withRelation) {
            // when the metricRatio is formatted (in determineMetricStats),
            // anything 1000+ is rounded to 1K+. ie 639,405 -> 639k
            // if there is a k, we follow with 'times' instead of an 'x'
            // ie 639k -> 639k times the.... vs 2.56 -> 2.56x the retweets per...
            if (_.isFinite(metricRatio)) {
                let decimal;
                if (metricRatio < METRIC_S_THRESHOLD) {
                    decimal = 1;
                    metricRatio = metricRatio.toFixed(1);
                } else {
                    decimal = 0;
                    if (metricRatio >= METRIC_M_THRESHOLD && metricRatio <= METRIC_L_THRESHOLD) {
                        // 100 - 500 nearest 50
                        metricRatio = Math.round(metricRatio / 50) * 50;
                    } else if (metricRatio > METRIC_L_THRESHOLD) {
                        // 500+ round to nearest 100
                        metricRatio = Math.round(metricRatio / 100) * 100;
                    }
                }
                const skipFormat = metricRatio < METRIC_XL_THRESHOLD;
                metricRatio = formatter.formatAbbreviated(metricRatio, null, {
                    decimals: decimal,
                    trailingZeros: false,
                    skipFormat: skipFormat
                });

                if (withRelation) {
                    // numbers with k (342k) should be followed with times instead of x (342kx)
                    return _.includes(metricRatio, 'k') ? `${metricRatio} times` : `${metricRatio}x`;
                }
                return metricRatio;
            }
        };

        /**
         * Formats a numeric value in an extended but still human digestible form. If val is less than 0.01, show
         * 4 decimals, otherwise show up to 2.
         * @param val The numeric value to format.
         */
        formatter.formatExpandedNumeric = function (val) {
            if (_.isUndefined(val) || val === null || isNaN(val) || val === Infinity) {
                return '--';
            }

            if (Math.abs(val) <= 0.01) {
                return formatter.formats.numericSmallValue(val);
            } else {
                return formatter.formats.numeric(val);
            }
        };

        /**
         * Returns the singular or plural label depending on `val`.
         * @param {string} singularLabel The label to use in the singular case.
         * @param {string} pluralLabel The label to use in the plural case.
         * @param {number} val The numeric value to use for testing plurality.
         * @returns {string} The appropriate (singular or plural) label.
         */
        formatter.numericLabel = function (singularLabel, pluralLabel, val) {
            return (val === 1 ? singularLabel : pluralLabel);
        };

        /**
         * Formats a number with a proceeding label.
         * @param {string} singularLabel The label to use in the singular case.
         * @param {string} pluralLabel The label to use in the plural case
         * @param {number} val The numeric value to be formatted. Must be an integer
         * @returns {string} A formatted string with appropriate label
         */
        formatter.formatLabeledNumber = function (singularLabel, pluralLabel, val) {
            return [
                formatter.formats.number(val),
                formatter.numericLabel(singularLabel, pluralLabel, val)
            ].join(' ');
        };

        /**
         * Formats a number into a decimal.
         * @param val The value to format as a decimal.
         * @returns A decimal.
         */
        formatter.formatDecimal = function (val) {

            if (val < 0.1 && val > 0) {

                return val.toFixed(2);
            }

            return val.toFixed(1);
        };

        formatter.formats = {
            number: d3.format(',d'),
            fixed: d3.format('0,.1f'),
            rateFixed: d3.format('0,.2f'),
            percent: d3.format('+0,.1%'),
            percentAbsolute: d3.format('0,.1%'),
            deltaRelative: d3.format('+0,%'),
            integer: d3.format(',.0f'),
            numeric: d3.format(',.2f'),
            numericSmallValue: d3.format(',.4f'),
            round: function (v) { return Math.round(v); },
            abbreviated: function (v, options) { return formatter.formatAbbreviated(v, undefined, options); },
            abbreviatedWithSign: function (v, options) { return formatter.formatAbbreviatedWithSign(v, undefined, options); },
            date: function (v, timezone) {
                if (timezone) {
                    const timezoneUtils = (typeof window === 'undefined') ? require('./utils/timezone.js') : window.RivalIQ.utils.timezone;
                    return timezoneUtils.getDateInTimezone(timezone, v).format('MMM D, YYYY');
                } else {
                    return moment.utc(v).format('MMM D, YYYY');
                }
            },
            ellipsify: formatter.ellipsify
        };

        // Remove when facebook fixes their under reporting insights bug that cause our organic calculations to come up negative
        formatter.noFacebookNegativeWrapper = function (formatter) {
            return function (v) {
                if (v < 0) {
                    return '--';
                } else {
                    return formatter(v);
                }
            };
        };

        // returns a wrapper that formats any falsy (non-zero) values as `--`, otherwise calls out to the passedin formatter to do the work.
        formatter.falsyDataWrapper = function (formatter) {
            return function (v) {
                if (v || v === 0) {
                    return formatter(v);
                } else {
                    return '--';
                }
            };
        };


        // returns a wrapper that formats any falsy (non-zero) values as `n/a`, otherwise calls out to the passedin formatter to do the work.
        formatter.naFalsyDataWrapper = function (formatter) {
            return function (v) {
                if (v || v === 0) {
                    return formatter(v);
                } else {
                    return 'N/A';
                }
            };
        };

        formatter.relativeMetricClass = function (value) {
            if (!_.isNumber(value) || value === 0) {
                return '';
            }

            return (value >= 0 ? 'metric-positive' : 'metric-negative');
        };

        // format dates for excel, numbers, etc.
        // http://stackoverflow.com/questions/804118/best-timestamp-format-for-csv-excel
        formatter.csvDate = function (date) {
            return moment.utc(date).format('YYYY-MM-DD HH:mm:ss');
        };
    };

    let App;
    let _;
    let D3;
    let Moment;
    let enums;

    if (typeof window === 'undefined') {

        App = {};
        _ = require('lodash');
        D3 = require('d3');
        Moment = require('../../lib/riq-moment.js');
        enums = require('../enums/metrics.js');
    } else {

        App = window.RivalIQ;
        _ = window._;
        D3 = window.d3;
        Moment = window.moment;
        enums = window.RivalIQ.enums;
    }

    // NOTE keep in mind that because Backbone, Marionette, and jQuery are not
    // server side dependencies we stub them with empty objects, and a function
    definition(App.utils, App, enums, {}, {}, _.identity, _, D3, Moment);

    if (typeof window === 'undefined') {

        module.exports = App.formatter;
    }

}());
